/**
 * RemindersSection Component
 * Displays a horizontal row of growth reminder quote cards.
 * Includes "Add Reminder" and "Show all" action links.
 */

import { ChevronRight, Plus } from "lucide-react"
import { useMemo, useState } from "react"
import { toast } from "sonner"
import { Carousel } from "@/components/carousel/Carousel"
import { useTranslation } from "@/language"
import { useGrowthReminders } from "../hooks/useGrowthReminders"
import { AddReminderModal } from "./AddReminderModal"
import { EditReminderModal } from "./EditReminderModal"
import { QuoteCard } from "./QuoteCard"

type ReminderCarouselItem =
  | { type: "add" }
  | {
      type: "reminder"
      id: string
      text: string
      backgroundImageKey?: string
      isSystemDefault?: boolean
    }
  | { type: "dummy"; text: string; backgroundImageKey: string }

export function RemindersSection() {
  const { t } = useTranslation("myRituality")
  const { reminders, isLoading, deleteReminder } = useGrowthReminders()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingReminder, setEditingReminder] = useState<{ id: string; text: string } | null>(null)

  const handleEdit = (id: string, text: string) => {
    setEditingReminder({ id, text })
  }

  const handleDelete = (id: string) => {
    deleteReminder(id, {
      onSuccess: () => {
        toast.success("Reminder deleted")
      },
      onError: () => {
        toast.error("Failed to delete reminder")
      },
    })
  }

  if (isLoading) {
    return (
      <section>
        <div className="h-6 w-64 bg-white/5 rounded animate-pulse mb-8" />
        <div className="flex gap-5 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="w-[310px] h-[310px] shrink-0 rounded-tl-[40px] rounded-br-[40px] bg-white/5 animate-pulse"
            />
          ))}
        </div>
      </section>
    )
  }

  return (
    <section>
      {/* Section header */}
      <div className="flex items-center justify-between mb-5 md:mb-8">
        <div className="flex flex-col gap-2.5">
          <h2 className="text-lg md:text-2xl font-semibold leading-[30px] uppercase opacity-90 text-primary">
            {t("reminders.title")}
          </h2>
          {reminders.length < 0 && (
            <p className="text-sm leading-6.25">
              Save a phrase, intention, or insight from your ritual.
            </p>
          )}
        </div>

        {reminders.length > 0 && (
          <div className="hidden md:flex items-center gap-6">
            {/* Add Reminder link */}
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:opacity-80 transition-opacity cursor-pointer"
            >
              <div className="bg-white text-base rounded-full">
                <Plus className="w-4 h-4" />
              </div>
              <span>{t("reminders.addReminder")}</span>
            </button>

            {/* Show all link */}
            <button
              type="button"
              disabled
              className="flex items-center gap-1 text-sm font-semibold text-primary opacity-40 cursor-not-allowed"
              title={t("reminders.comingSoon")}
            >
              <span>{t("reminders.showAll")}</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Quote cards row */}
      <ReminderCarousel
        reminders={reminders}
        onAdd={() => setIsModalOpen(true)}
        onEdit={handleEdit}
        onDelete={handleDelete}
        t={t}
      />

      <AddReminderModal open={isModalOpen} onOpenChange={setIsModalOpen} />
      <EditReminderModal
        open={!!editingReminder}
        onOpenChange={open => !open && setEditingReminder(null)}
        reminderId={editingReminder?.id}
        defaultText={editingReminder?.text}
      />

      {reminders.length > 0 && (
        <div className="flex md:hidden items-center gap-7.5 md:gap-6 mt-7.5">
          {/* Add Reminder link */}
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-1.25 text-sm font-semibold text-primary hover:opacity-80 transition-opacity cursor-pointer"
          >
            <div className="bg-white text-base rounded-full">
              <Plus className="w-5 h-5 md:w-4 md:h-4" />
            </div>
            <span>{t("reminders.addReminder")}</span>
          </button>

          {/* Show all link */}
          <button
            type="button"
            disabled
            className="flex items-center gap-1.25 text-sm font-semibold text-primary opacity-40 cursor-not-allowed"
            title={t("reminders.comingSoon")}
          >
            <span>{t("reminders.showAll")}</span>
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </section>
  )
}

function ReminderCarousel({
  reminders,
  onAdd,
  onEdit,
  onDelete,
  t,
}: {
  reminders: Array<{
    id: string
    text: string
    backgroundImageKey?: string
    isSystemDefault?: boolean
  }>
  onAdd: () => void
  onEdit: (id: string, text: string) => void
  onDelete: (id: string) => void
  t: (key: string) => string
}) {
  const carouselItems: ReminderCarouselItem[] = useMemo(() => {
    if (reminders.length > 0) {
      return reminders.slice(0, 4).map(r => ({
        type: "reminder" as const,
        id: r.id,
        text: r.text,
        backgroundImageKey: r.backgroundImageKey,
        isSystemDefault: r.isSystemDefault,
      }))
    }
    return [
      { type: "add" as const },
      { type: "dummy" as const, text: t("reminders.dummy1"), backgroundImageKey: "abstract-1" },
      { type: "dummy" as const, text: t("reminders.dummy2"), backgroundImageKey: "abstract-2" },
      { type: "dummy" as const, text: t("reminders.dummy3"), backgroundImageKey: "abstract-3" },
    ]
  }, [reminders, t])

  return (
    <Carousel
      data={carouselItems}
      renderItem={item => {
        if (item.type === "add") {
          return (
            <div
              className="relative w-[310px] h-[310px] overflow-hidden rounded-tl-[40px] rounded-br-[40px] bg-[#0E0E0E] border border-dashed border-[#F2F0EB33] cursor-pointer group"
              onClick={onAdd}
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center p-8">
                <div className="w-17.5 h-17.5 rounded-full border border-[#F2F0EB26] flex items-center justify-center mb-5 group-hover:border-[#F2F0EB] transition-colors">
                  <Plus className="w-11.5 h-11.5 text-[#F2F0EB]" />
                </div>
                <p className="text-sm font-semibold text-white text-center uppercase">
                  {t("reminders.addYourOwn")}
                </p>
              </div>
            </div>
          )
        }
        if (item.type === "dummy") {
          return (
            <QuoteCard
              text={item.text}
              backgroundImageKey={item.backgroundImageKey}
              showMenu={false}
            />
          )
        }
        return (
          <QuoteCard
            text={item.text}
            backgroundImageKey={item.backgroundImageKey ?? "abstract-1"}
            showMenu={!item.isSystemDefault}
            onEdit={() => onEdit(item.id, item.text)}
            onDelete={() => onDelete(item.id)}
          />
        )
      }}
      itemWidth="310px"
      contentClassName="gap-5 touch-pan-x"
      opts={{ align: "start", loop: false }}
    />
  )
}
